import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Slider, IconButton, Typography, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import TranslateIcon from '@mui/icons-material/Translate';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AddIcon from '@mui/icons-material/Add';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/Delete';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import WaveformDisplay from './WaveForm';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { API_CONFIG } from '../../config/constants';
import RemoveIcon from '@mui/icons-material/Remove';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AudioVolumeWidget from './AudioVolumeWidget';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import SubtitlesOffIcon from '@mui/icons-material/SubtitlesOff';
import { projectService } from '../../services/project.service';
import VideocamIcon from '@mui/icons-material/Videocam';

const TRACK_HEIGHT = 40;
const TIMELINE_PADDING = 10;
const CURSOR_WIDTH = 2;
const SPEAKER_LABEL_WIDTH = 200;
const TIMESCALE_HEIGHT = 22;

const PRIMARY_COLOR = '#6366f1';
const SECONDARY_COLOR = '#4f46e5';

function Timeline({ currentTime,
    duration,
    onSeek,
    transcript,
    selectedBlock,
    onBlockSelect,
    onTranscriptUpdate,
    mutedTracks,
    toggleMute,
    projectId,
    onSave,
    isVideoAudioEnabled,
    onResetAudioCache,
    handleAudioReload,
    toggleVideoAudio,
    hasVideo,
    isPlaying,
    checkUndoAvailability,
    subtitleSettings,
    lipsyncProcessingInfo,
    onSubtitleSettingsChange }) {
    const containerRef = useRef(null);
    const timelineRef = useRef(null);
    const [zoom, setZoom] = useState(2);
    const [scrollPercentage, setScrollPercentage] = useState(0);
    const [hoveredBlock, setHoveredBlock] = useState(null);
    const [draggingBlock, setDraggingBlock] = useState(null);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartTime, setDragStartTime] = useState(0);
    const [isCutMode, setIsCutMode] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(currentTime);
    const [speakerOrder, setSpeakerOrder] = useState([]);
    const [showTranslatedText, setShowTranslatedText] = useState(true);
    const [selectedBlocks, setSelectedBlocks] = useState([]);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const hasMovedRef = useRef(false);
    const waveformRefs = useRef({});
    const [maxSpeaker, setMaxSpeaker] = useState(0);
    // Ajouter un nouvel état pour tracker les speakers ajoutés manuellement
    const [manualSpeakers, setManualSpeakers] = useState([]);
    const [backgroundAudio, setBackgroundAudio] = useState({});
    const [isCutProcessing, setIsCutProcessing] = useState(false);
    const [areSubtitlesEnabled, setAreSubtitlesEnabled] = useState(
        subtitleSettings ? subtitleSettings.enabled : true
    );
    const [showSilentSegments, setShowSilentSegments] = useState(false);
    const [showVideoTrack, setShowVideoTrack] = useState(true);


    useEffect(() => {
        if (subtitleSettings) {
            setAreSubtitlesEnabled(subtitleSettings.enabled);
        }
    }, [subtitleSettings]);


    const toggleSubtitles = async () => {
        const newEnabledState = !areSubtitlesEnabled;
        setAreSubtitlesEnabled(newEnabledState);

        // Mettre à jour les paramètres de sous-titres via la callback fournie
        if (onSubtitleSettingsChange) {
            const newSettings = {
                ...subtitleSettings,
                enabled: newEnabledState
            };

            onSubtitleSettingsChange(newSettings);

            // Sauvegarder les nouveaux paramètres sur le serveur
            try {
                // ✅ FIX: Get current subtitles data first
                const currentData = await projectService.getSubtitles(projectId);

                // ✅ FIX: Send both settings and subtitles
                const result = await projectService.updateSubtitleSettings(
                    projectId,
                    {
                        settings: newSettings,
                        subtitles: currentData.subtitles || []  // Include existing subtitles
                    }
                );

                console.log("Subtitle settings saved:", result);
            } catch (error) {
                console.error("Error saving subtitle settings:", error);
                // Optionally revert the state if save fails
                setAreSubtitlesEnabled(!newEnabledState);
                alert("Erreur lors de la sauvegarde des paramètres de sous-titres");
            }
        }
    };

    const isValidSegment = (segment) => {
        return segment &&
            typeof segment === 'object' &&
            typeof segment.start === 'number' &&
            typeof segment.end === 'number' &&
            segment.speaker;
    };

    const isSegmentProcessingLipsync = useCallback((segment) => {
        if (!lipsyncProcessingInfo || !segment) return false;

        const { processingSegments, videoAudioSegments } = lipsyncProcessingInfo;

        // Vérifier si ce segment est dans la Map des segments en traitement
        for (let [index, processingInfo] of processingSegments) {
            const processingSegment = videoAudioSegments[index];
            if (processingSegment &&
                processingSegment.segment_file === segment.segment_file &&
                processingSegment.start === segment.start &&
                processingSegment.end === segment.end) {
                return processingInfo.status === 'processing';
            }
        }
        return false;
    }, [lipsyncProcessingInfo]);

    const hasProcessingSegments = useCallback((segments) => {
        return segments.some(segment => isSegmentProcessingLipsync(segment));
    }, [isSegmentProcessingLipsync]);


    // Fonction pour la gestion de la suppression d'une piste
    const removeSpeaker = async (speaker) => {
        // Vérifier qu'on garde au moins une piste
        if (speakerOrder.length <= 1) {
            alert("Impossible de supprimer la dernière piste.");
            return;
        }

        // Trouver la piste précédente
        const speakerIndex = speakerOrder.indexOf(speaker);
        const previousSpeaker = speakerIndex > 0
            ? speakerOrder[speakerIndex - 1]
            : speakerOrder[1]; // Si c'est le premier, prendre le suivant

        // Vérifier si le speaker a des segments
        const hasSpeakerSegments = transcript.some(segment => segment.speaker === speaker);

        if (hasSpeakerSegments) {
            // Transférer automatiquement les segments vers la piste précédente
            const updatedTranscript = transcript.map(segment => {
                if (segment.speaker === speaker) {
                    return { ...segment, speaker: previousSpeaker };
                }
                return segment;
            });

            // Mettre à jour le transcript
            onTranscriptUpdate(updatedTranscript);
            await onSave(updatedTranscript);
        }

        // Supprimer le speaker
        setManualSpeakers(prev => prev.filter(s => s !== speaker));
        setSpeakerOrder(prev => prev.filter(s => s !== speaker));
    };

    const handleAddSpeaker = () => {
        // Trouver le numéro maximal actuel parmi les pistes existantes
        const usedNumbers = speakerOrder
            .filter(s => s.startsWith('SPEAKER')) // Ne considérer que les SPEAKER
            .map(s => parseInt(s.split('_')[1])); // Extraire les numéros

        // Si aucun SPEAKER n'existe, commencer à 0
        const maxNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) : -1;

        // Le prochain numéro est maxNumber + 1
        const nextNumber = maxNumber + 1;

        const newSpeaker = `SPEAKER_${nextNumber}`;

        // Ajouter la nouvelle piste à manualSpeakers
        setManualSpeakers(prev => [...prev, newSpeaker]);

        // Mettre à jour speakerOrder en plaçant la nouvelle piste avant les BACKGROUND
        const updatedSpeakerOrder = [
            ...speakerOrder.filter(s => s.startsWith('SPEAKER')),
            newSpeaker,
            ...speakerOrder.filter(s => s.startsWith('BACKGROUND')),
        ];
        setSpeakerOrder(updatedSpeakerOrder);

        // Mettre à jour maxSpeaker
        setMaxSpeaker(nextNumber);
    };


    const handleUnlinkSegment = async () => {
        if (!selectedBlock || !isVideoSegment(selectedBlock) || selectedBlock.is_silent === true) {
            return;
        }

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/unlink_segment/${projectId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    segment: selectedBlock,
                    transcript: transcript
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to unlink segment');
            }

            const result = await response.json();

            // Mettre à jour le transcript
            onTranscriptUpdate(result.updatedTranscript);

            // Désélectionner le bloc
            onBlockSelect(null);

            // Sauvegarder
            await onSave(result.updatedTranscript);

            // Vérifier la disponibilité d'annulation
            if (typeof checkUndoAvailability === 'function') {
                setTimeout(() => {
                    checkUndoAvailability();
                }, 500);
            }

        } catch (error) {
            console.error('Error unlinking segment:', error);
            alert("❌ Une erreur s'est produite lors de la séparation du segment.");
        }
    };

    // Modifier l'effet qui gère les speakers pour maintenir une numérotation séquentielle
    useEffect(() => {

        if (!Array.isArray(transcript)) {
            console.warn('Transcript is not an array:', transcript);
            return;
        }

        // Filtrer les segments valides AVANT tout traitement
        const validTranscript = transcript.filter(isValidSegment);

        // Extraire les speakers uniques du transcript

        const visibleTranscript = showSilentSegments
            ? transcript
            : transcript.filter(item => item.is_silent !== true);

        // Extraire les speakers uniques du transcript filtré
        const transcriptSpeakers = [...new Set(visibleTranscript.map(item => item.speaker))];

        // Séparer les speakers normaux et les backgrounds
        const [backgroundSpeakers, normalSpeakers] = transcriptSpeakers.reduce((acc, speaker) => {
            speaker.startsWith('BACKGROUND') ? acc[0].push(speaker) : acc[1].push(speaker);
            return acc;
        }, [[], []]);

        // Trier les speakers normaux par numéro
        const sortedNormal = normalSpeakers.sort((a, b) => {
            const numA = parseInt(a.split('_')[1]);
            const numB = parseInt(b.split('_')[1]);
            return numA - numB;
        });

        // Trier les backgrounds par numéro
        const sortedBackground = backgroundSpeakers.sort((a, b) => {
            const numA = parseInt(a.split('_')[1]);
            const numB = parseInt(b.split('_')[1]);
            return numA - numB;
        });

        // Combiner avec les speakers manuels (en évitant les doublons)
        const allSpeakers = [
            ...new Set([
                ...sortedNormal,
                ...manualSpeakers.filter(s => s.startsWith('SPEAKER')), // Ajouter uniquement les SPEAKER manuels
                ...sortedBackground,
            ]),
        ];

        // Mettre à jour maxSpeaker (uniquement pour les SPEAKER)
        const speakerNumbers = allSpeakers
            .filter(s => s.startsWith('SPEAKER'))
            .map(s => parseInt(s.split('_')[1]));
        const maxNum = speakerNumbers.length > 0 ? Math.max(...speakerNumbers) : -1;
        setMaxSpeaker(maxNum);

        // Mettre à jour l'ordre des pistes : SPEAKER en haut, BACKGROUND en bas
        const videoSegments = transcript.filter(item =>
            item.video_segment_file || item.video_file
        );

        // Mettre à jour l'ordre des pistes : VIDEO en haut, puis SPEAKER, puis BACKGROUND
        const orderedSpeakers = [
            // ➕ AJOUTER la piste vidéo si il y a des segments vidéo
            ...(showVideoTrack && videoSegments.length > 0 ? ['VIDEO'] : []),
            ...new Set([
                ...sortedNormal,
                ...manualSpeakers.filter(s => s.startsWith('SPEAKER')),
                ...sortedBackground,
            ]),
        ];

        // Trier orderedSpeakers pour maintenir l'ordre correct
        // ➕ MODIFIER : Trier orderedSpeakers avec VIDEO toujours en premier
        const sortedOrderedSpeakers = orderedSpeakers.sort((a, b) => {
            // VIDEO toujours en premier
            if (a === 'VIDEO') return -1;
            if (b === 'VIDEO') return 1;

            // Ensuite, trier les SPEAKER et BACKGROUND
            if (a.startsWith('SPEAKER') && b.startsWith('SPEAKER')) {
                const numA = parseInt(a.split('_')[1]);
                const numB = parseInt(b.split('_')[1]);
                return numA - numB; // Trier les SPEAKER par numéro croissant
            } else if (a.startsWith('BACKGROUND') && b.startsWith('BACKGROUND')) {
                const numA = parseInt(a.split('_')[1]);
                const numB = parseInt(b.split('_')[1]);
                return numA - numB; // Trier les BACKGROUND par numéro croissant
            } else if (a.startsWith('SPEAKER')) {
                return -1; // Les SPEAKER viennent avant les BACKGROUND
            } else {
                return 1; // Les BACKGROUND viennent après les SPEAKER
            }
        });
        setSpeakerOrder(sortedOrderedSpeakers);

    }, [transcript, manualSpeakers, showSilentSegments]);

    const handleTimelineMouseMove = useCallback((event) => {
        if (timelineRef.current) {
            const rect = timelineRef.current.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const totalWidth = timelineRef.current.scrollWidth;
            const visibleWidth = rect.width;
            const scrollLeft = timelineRef.current.scrollLeft;

            // Calculer le pourcentage total (en tenant compte du scroll)
            const totalPercent = ((scrollLeft + clickX) / totalWidth);
            const clickTime = totalPercent * duration;

            if (isCutMode || isMouseDown) {
                setCursorPosition(Math.max(0, Math.min(duration, clickTime)));
            }
        }
    }, [isCutMode, isMouseDown, duration]);

    const handleTimelineMouseDown = useCallback((event) => {
        setIsMouseDown(true);
        handleTimelineMouseMove(event);
    }, [handleTimelineMouseMove]);

    const handleTimelineMouseUp = useCallback(() => {
        setIsMouseDown(false);
    }, []);

    const handleTimelineMouseLeave = useCallback(() => {
        if (!isCutMode) {
            setCursorPosition(currentTime);
        }
        setHoveredBlock(null);
    }, [isCutMode, currentTime]);

    useEffect(() => {
        if (!isCutMode) {
            setCursorPosition(currentTime);
        }
    }, [isCutMode, currentTime]);

    useEffect(() => {
        if (timelineRef.current) {
            timelineRef.current.addEventListener('mousemove', handleTimelineMouseMove);
            timelineRef.current.addEventListener('mouseleave', handleTimelineMouseLeave);
            timelineRef.current.addEventListener('mousedown', handleTimelineMouseDown);
            document.addEventListener('mouseup', handleTimelineMouseUp);
        }
        return () => {
            if (timelineRef.current) {
                timelineRef.current.removeEventListener('mousemove', handleTimelineMouseMove);
                timelineRef.current.removeEventListener('mouseleave', handleTimelineMouseLeave);
                timelineRef.current.removeEventListener('mousedown', handleTimelineMouseDown);
            }
            document.removeEventListener('mouseup', handleTimelineMouseUp);
        };
    }, [handleTimelineMouseMove, handleTimelineMouseLeave, handleTimelineMouseDown, handleTimelineMouseUp]);

    const handleMouseDown = useCallback((event, block) => {
        // 🛡️ VÉRIFICATION DE SÉCURITÉ AJOUTÉE
        if (!block || !isValidSegment(block)) {
            return; // Sortir immédiatement si le bloc n'est pas valide
        }

        // 🚫 NOUVEAU : Bloquer si le segment est en cours de lipsync
        if (isSegmentProcessingLipsync(block)) {
            event.stopPropagation();
            alert("⏳ Ce segment est en cours de traitement lipsync. Veuillez patienter.");
            return;
        }

        // 🚫 NE PAS démarrer le drag si c'est une sélection multiple
        const isMultiSelect = event.ctrlKey || event.metaKey || event.shiftKey;

        if (!isCutMode && !(block.video_segment_file || block.video_file) && !isMultiSelect) {
            event.stopPropagation();
            setDraggingBlock(block);
            setDragStartX(event.clientX);
            setDragStartTime(block.start); // Plus besoin de vérifier if (block) car on l'a déjà fait

            setIsDragging(true);
            hasMovedRef.current = false;
            setIsMouseDown(true);
        }
    }, [isCutMode]);

    const handleMouseMove = useCallback((event) => {
        if (isMouseDown && draggingBlock && timelineRef.current) {
            const deltaX = event.clientX - dragStartX;
            const rect = timelineRef.current.getBoundingClientRect();

            // Calculer la piste sur laquelle on se trouve
            const clickY = event.clientY - rect.top;
            const trackIndex = Math.floor((clickY - TIMELINE_PADDING - TIMESCALE_HEIGHT) / TRACK_HEIGHT);
            const newSpeaker = trackIndex >= 0 && trackIndex < speakerOrder.length ?
                speakerOrder[trackIndex] : draggingBlock.speaker;

            // Mettre à jour hasMovedRef immédiatement si le mouvement dépasse le seuil
            if (Math.abs(deltaX) > 2) {
                hasMovedRef.current = true;
            }

            // Mise à jour de la position si on dépasse le seuil ou change de speaker
            if (Math.abs(deltaX) > 2 || draggingBlock.speaker !== newSpeaker) {
                if (!isDragging) {
                    setIsDragging(true);
                }

                const timelineWidth = rect.width;
                const visibleDuration = duration / zoom;
                const pixelsPerSecond = timelineWidth / visibleDuration;
                const deltaTime = deltaX / pixelsPerSecond;

                const newStart = Math.max(0, Math.min(duration - (draggingBlock.end - draggingBlock.start), dragStartTime + deltaTime));
                const newEnd = newStart + (draggingBlock.end - draggingBlock.start);

                // Vérifie si le speaker change
                const speakerChanged = newSpeaker !== draggingBlock.speaker;

                const updatedTranscript = transcript.map(t => {
                    if (t.text === draggingBlock.text) {
                        // Si c'est le segment qu'on déplace
                        const updatedSegment = {
                            ...t,
                            start: newStart,
                            end: newEnd,
                            speaker: newSpeaker
                        };

                        // Si c'est un changement de speaker, ajouter/mettre à jour original_speaker
                        if (speakerChanged) {
                            // Si original_speaker existe déjà, ne pas l'écraser
                            // sinon, mettre le speaker actuel comme original_speaker
                            if (!updatedSegment.original_speaker) {
                                updatedSegment.original_speaker = draggingBlock.speaker;
                            }
                        }

                        return updatedSegment;
                    }
                    return t;
                });

                onTranscriptUpdate(updatedTranscript);

                // Effectuer un save immédiat uniquement si le speaker a changé
                if (speakerChanged) {
                    onSave(updatedTranscript);

                    // Mise à jour de la référence du draggingBlock pour éviter des saves multiples pour le même changement
                    draggingBlock.speaker = newSpeaker;
                }

                if (event.clientX < rect.left + 20) {
                    timelineRef.current.scrollLeft -= 10;
                } else if (event.clientX > rect.right - 20) {
                    timelineRef.current.scrollLeft += 10;
                }
            }
        }
    }, [draggingBlock, dragStartX, dragStartTime, duration, zoom, transcript, onTranscriptUpdate, speakerOrder]);
    const handleMouseUp = useCallback(() => {
        if (draggingBlock && isDragging && hasMovedRef.current) {
            onSave();
        }
        setDraggingBlock(null);
        setIsDragging(false);
        setIsMouseDown(false); // Ajout de cette ligne
        hasMovedRef.current = false;
    }, [draggingBlock, isDragging, onSave]);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousedown', handleMouseDown);

        };
    }, [handleMouseMove, handleMouseUp]);

    const handleCutModeToggle = () => {
        setIsCutMode(!isCutMode);
    };


    // Utilitaire pour comparer deux segments
    const areSegmentsEqual = (seg1, seg2) => {
        return seg1.start === seg2.start &&
            seg1.end === seg2.end &&
            seg1.speaker === seg2.speaker &&
            seg1.segment_file === seg2.segment_file;
    };

    const findModifiedSegments = (oldTranscript, newTranscript) => {
        // Trouver spécifiquement les segments affectés par le merge
        const modifiedSegments = [];

        // 1. Trouver les deux segments qui ont été fusionnés (ils n'existeront plus dans newTranscript)
        const removedSegments = oldTranscript.filter(oldSeg =>
            !newTranscript.some(newSeg => newSeg.segment_file === oldSeg.segment_file)
        );

        // 2. Trouver le nouveau segment fusionné (il n'existera pas dans oldTranscript)
        const newSegments = newTranscript.filter(newSeg =>
            !oldTranscript.some(oldSeg => oldSeg.segment_file === newSeg.segment_file)
        );

        // 3. Ajouter uniquement ces segments spécifiques aux segments modifiés
        modifiedSegments.push(...newSegments);

        console.log("Segments fusionnés:", modifiedSegments);
        return modifiedSegments;
    };

    const performCut = async (cutTime, speaker) => {
        try {
            // 🚫 VÉRIFICATION : Empêcher la coupe sur la piste VIDEO
            if (speaker === 'VIDEO') {
                alert("❌ Impossible de couper les segments vidéo.");
                return;
            }

            // 🚫 VÉRIFICATION : Vérifier si le point de coupe touche un segment
            const segmentAtCutPoint = transcript.find(segment =>
                segment.speaker === speaker &&
                cutTime >= segment.start &&
                cutTime < segment.end
            );

            if (!segmentAtCutPoint) {
                alert("❌ Aucun segment trouvé à cet endroit.");
                return;
            }

            // 🚫 NOUVEAU : Vérifier si le segment est en cours de lipsync
            if (isSegmentProcessingLipsync(segmentAtCutPoint)) {
                alert("⏳ Ce segment est en cours de traitement lipsync. Impossible de le couper pour le moment.");
                return;
            }

            // 🎥 NOUVEAU : Détecter si c'est un segment avec vidéo liée
            const hasLinkedVideo = !!(segmentAtCutPoint.video_segment_file || segmentAtCutPoint.video_file);

            // Si c'est un segment vidéo silencieux (is_silent), on ne peut pas le couper
            if (segmentAtCutPoint.is_silent === true) {
                alert("❌ Impossible de couper un segment vidéo silencieux.");
                return;
            }

            // 🎥 INFO : Informer l'utilisateur si on va couper audio + vidéo
            if (hasLinkedVideo) {
                console.log("🎬 Cutting a segment with linked video");
            }

            // Activez le flag pour bloquer le curseur
            setIsCutProcessing(true);

            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/cut_audio/${projectId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    transcript: transcript,
                    cutTime: cutTime,
                    speaker: speaker,
                    prioritizeOriginalSpeaker: true
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Network response was not ok');
            }

            const result = await response.json();

            // 🎥 NOUVEAU : Vérifier si le backend a coupé une vidéo
            if (result.hadVideo) {
                console.log("✅ Video was successfully cut along with audio");
            }

            // Identifier uniquement les segments modifiés
            const modifiedSegments = findModifiedSegments(transcript, result.updatedTranscript);

            // Mettre à jour le transcript complet
            onTranscriptUpdate(result.updatedTranscript);

            // Recharger uniquement les segments modifiés
            const speakersToReload = [];

            // Pour chaque segment modifié, déterminer le bon speaker pour le rechargement audio
            modifiedSegments.forEach(segment => {
                // Utiliser original_speaker pour le chemin audio si disponible
                const audioPathSpeaker = segment.original_speaker || segment.speaker;
                if (!speakersToReload.includes(audioPathSpeaker)) {
                    speakersToReload.push(audioPathSpeaker);
                }
            });

            // Recharger l'audio pour chaque speaker concerné
            speakersToReload.forEach(audioSpeaker => {
                const speakerSegments = modifiedSegments.filter(seg =>
                    (seg.original_speaker || seg.speaker) === audioSpeaker
                );

                speakerSegments.forEach(segment => {
                    if (handleAudioReload) {
                        // Utiliser le speaker pour les chemins d'accès aux fichiers audio
                        handleAudioReload(audioSpeaker, segment);
                    }
                });
            });

            // Vérifier la disponibilité d'annulation après l'opération de découpe
            if (typeof checkUndoAvailability === 'function') {
                setTimeout(() => {
                    checkUndoAvailability();
                }, 500);
            }

        } catch (error) {
            console.error('Error:', error);
            alert("❌ Une erreur s'est produite lors de la coupe.");
        } finally {
            // Désactivez le flag pour débloquer le curseur
            setIsCutProcessing(false);
        }
    };

    const handleTimelineClick = (event) => {
        if (timelineRef.current) {
            const rect = timelineRef.current.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const clickY = event.clientY - rect.top;
            const totalWidth = timelineRef.current.scrollWidth;
            const scrollLeft = timelineRef.current.scrollLeft;

            const totalPercent = ((scrollLeft + clickX) / totalWidth);
            const clickTime = totalPercent * duration;

            const trackIndex = Math.floor((clickY - TIMELINE_PADDING - TIMESCALE_HEIGHT) / TRACK_HEIGHT);
            const speaker = speakerOrder[trackIndex];

            if (isCutMode && !isCutProcessing) {
                if (speaker === 'VIDEO') {
                    alert("❌ Impossible de couper la piste vidéo.");
                    return;
                }

                // Vérifier si on clique sur un segment
                const clickedSegment = transcript.find(segment =>
                    segment.speaker === speaker &&
                    clickTime >= segment.start &&
                    clickTime < segment.end
                );

                if (!clickedSegment) {
                    alert("❌ Aucun segment trouvé à cet endroit.");
                    return;
                }

                // Si c'est un segment vidéo silencieux, on ne peut pas le couper
                if (clickedSegment.is_silent === true) {
                    alert("❌ Impossible de couper un segment vidéo silencieux.");
                    return;
                }

                // 🎥 NOUVEAU : Si c'est un segment avec vidéo liée, informer l'utilisateur
                if (clickedSegment.video_segment_file || clickedSegment.video_file) {
                    // On peut couper, mais informer que ça va couper audio + vidéo
                    console.log("🎬 About to cut a segment with linked audio and video");
                }

                // Appeler performCut avec le temps et le speaker
                performCut(clickTime, speaker);
            } else if (!isCutMode) {
                let clickedBlock = null;

                // Pour la piste VIDEO, chercher dans les segments vidéo
                if (speaker === 'VIDEO') {
                    clickedBlock = transcript.find(t =>
                        (t.video_segment_file || t.video_file || t.is_silent) &&
                        clickTime >= t.start &&
                        clickTime < t.end
                    );
                } else {
                    // Pour les autres pistes, chercher les segments du speaker
                    clickedBlock = transcript.find(t =>
                        t.speaker === speaker &&
                        clickTime >= t.start &&
                        clickTime < t.end
                    );
                }

                if (clickedBlock) {
                    handleBlockSelect(clickedBlock, event);
                } else {
                    onBlockSelect(null);
                    setSelectedBlocks([]);
                }
            }

            onSeek(Math.max(0, Math.min(duration, clickTime)));
        }
    };


    const getColor = (speaker) => {
        if (speaker === 'VIDEO') {
            return '#9333EA'; // Violet pour la vidéo
        }

        const colors = ["#4B90F5", "#45C75E", "#F5A623", "#FF6B6B"];
        const orderedSpeakers = [...new Set(transcript.map(item => item.speaker))].sort();
        const speakerIndex = orderedSpeakers.indexOf(speaker);
        return colors[speakerIndex % colors.length];
    };

    const handleZoomChange = (event, newValue) => {
        const oldZoom = zoom;
        setZoom(newValue);
        if (timelineRef.current) {
            const visibleCenter = scrollPercentage + (1 / (2 * oldZoom));
            const newScrollPercentage = visibleCenter - (1 / (2 * newValue));
            setScrollPercentage(Math.max(0, Math.min(1 - (1 / newValue), newScrollPercentage)));
            timelineRef.current.scrollLeft = newScrollPercentage * timelineRef.current.scrollWidth;
        }
    };

    const handleScroll = (event) => {
        const maxScroll = event.target.scrollWidth - event.target.clientWidth;
        setScrollPercentage(event.target.scrollLeft / maxScroll);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const ms = Math.floor((seconds % 1) * 100);
        return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
    };

    const renderTimeScale = () => {
        const visibleDuration = duration / zoom;

        // Ajuster l'intervalle en fonction du zoom
        let interval;
        if (visibleDuration <= 30) {
            interval = 1;
        } else if (visibleDuration <= 60) {
            interval = 5;
        } else if (visibleDuration <= 300) {
            interval = 15;
        } else if (visibleDuration <= 900) {
            interval = 30;
        } else {
            interval = 60;
        }

        const timeScaleMarkers = [];
        const numMarkers = Math.ceil(duration / interval);
        const minSpaceBetweenLabels = 50; // Espace minimum en pixels entre les labels
        const timelineWidth = timelineRef.current?.clientWidth || 1000;
        const pixelsPerPercent = timelineWidth / 100;

        for (let i = 0; i <= numMarkers; i++) {
            const time = i * interval;
            if (time <= duration) {
                const left = `${(time / duration) * 100}%`;
                const leftPixels = (time / duration) * 100 * pixelsPerPercent * zoom;

                // Vérifier si ce marqueur serait trop proche du précédent
                const previousMarkerPixels = ((i - 1) * interval / duration) * 100 * pixelsPerPercent * zoom;
                const shouldShow = i === 0 || (leftPixels - previousMarkerPixels) >= minSpaceBetweenLabels;

                timeScaleMarkers.push(
                    <Box
                        key={time}
                        sx={{
                            position: 'absolute',
                            left: left,
                            transform: 'translateX(-50%)',
                            marginTop: 1,
                            height: TIMESCALE_HEIGHT + 1,
                            display: shouldShow ? 'flex' : 'none',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.65rem',
                                color: 'rgba(255,255,255,0.7)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {formatTime(time)}
                        </Typography>
                        <Box
                            sx={{
                                width: 1,
                                height: 5,
                                bgcolor: 'rgba(255,255,255,0.5)',
                                marginTop: '2px'
                            }}
                        />
                    </Box>
                );
            }
        }

        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden'
                }}
            >
                {timeScaleMarkers}
            </Box>
        );
    };

    const toggleTranslatedText = () => {
        setShowTranslatedText(!showTranslatedText);
    };

    const lightenColor = (color, amount) => {
        return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
    };

    const handleBlockSelect = (block, event) => {
        // ⚠️ IMPORTANT : Empêcher la propagation pour éviter les conflits
        event.stopPropagation();

        // 🔍 Vérifier explicitement les touches de modification
        const isMultiSelect = event.ctrlKey || event.metaKey || event.shiftKey;

        console.log('🔍 Selection event:', {
            block: block.text?.substring(0, 20) + '...',
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey,
            isMultiSelect,
            currentSelection: selectedBlocks.length
        });

        if (isMultiSelect) {
            setSelectedBlocks(prev => {
                // Si le bloc est déjà sélectionné, le désélectionner
                if (prev.some(b => b === block || (b.segment_file && b.segment_file === block.segment_file))) {
                    const newSelection = prev.filter(b => b !== block &&
                        !(b.segment_file && b.segment_file === block.segment_file));
                    console.log('➖ Removing from selection, new count:', newSelection.length);
                    return newSelection;
                } else {
                    // Limiter à 2 segments maximum pour le merge/link
                    const newSelection = prev.length >= 2 ? [prev[1], block] : [...prev, block];
                    console.log('➕ Adding to selection, new count:', newSelection.length);
                    return newSelection;
                }
            });

            // Garder le bloc comme selectedBlock principal
            onBlockSelect(block);
        } else {
            // Sélection simple : remplacer toute la sélection
            console.log('🔄 Single selection');
            setSelectedBlocks([block]);
            onBlockSelect(block);
        }
    };

    // Fonction pour vérifier si deux segments peuvent être liés
    const canLinkSegments = (blocks) => {
        if (blocks.length !== 2) return false;

        // Un doit être vidéo (silencieux), l'autre audio
        const videoBlock = blocks.find(b =>
            (b.speaker === 'VIDEO' || b.is_silent === true) &&
            (b.video_segment_file || b.video_file)
        );
        const audioBlock = blocks.find(b =>
            b.speaker !== 'VIDEO' &&
            !b.is_silent &&
            b.segment_file
        );

        if (!videoBlock || !audioBlock) return false;

        // Vérifier que les timestamps correspondent (avec une petite tolérance)
        const tolerance = 0.1; // 100ms de tolérance
        const startMatch = Math.abs(videoBlock.start - audioBlock.start) < tolerance;
        const endMatch = Math.abs(videoBlock.end - audioBlock.end) < tolerance;

        return startMatch && endMatch;
    };

    const handleLinkSegments = async () => {
        if (selectedBlocks.length !== 2) {
            alert("⚠️ Veuillez sélectionner exactement un segment vidéo et un segment audio.");
            return;
        }

        const videoBlock = selectedBlocks.find(b => b.speaker === 'VIDEO' || b.is_silent === true);
        const audioBlock = selectedBlocks.find(b => b.speaker !== 'VIDEO' && !b.is_silent);

        if (!videoBlock || !audioBlock) {
            alert("⚠️ Vous devez sélectionner un segment vidéo et un segment audio.");
            return;
        }

        // 🚫 NOUVEAU : Vérifier si des segments sont en cours de lipsync
        if (hasProcessingSegments(selectedBlocks)) {
            alert("⏳ Un ou plusieurs segments sont en cours de traitement lipsync. Impossible de les lier pour le moment.");
            return;
        }


        try {
            // Copier les propriétés vidéo vers le segment audio
            const updatedTranscript = transcript.map(segment => {
                if (segment === audioBlock) {
                    return {
                        ...segment,
                        video_segment_file: videoBlock.video_segment_file,
                        video_file: videoBlock.video_file,
                        video_segment_url: videoBlock.video_segment_url
                    };
                }
                // Supprimer le segment vidéo silencieux
                return segment;
            }).filter(segment => segment !== videoBlock);

            // Mettre à jour et sauvegarder
            onTranscriptUpdate(updatedTranscript);
            await onSave(updatedTranscript);

            // Réinitialiser la sélection
            setSelectedBlocks([]);
            onBlockSelect(null);

            // Vérifier la disponibilité d'annulation
            if (typeof checkUndoAvailability === 'function') {
                setTimeout(() => {
                    checkUndoAvailability();
                }, 500);
            }

        } catch (error) {
            console.error('Error linking segments:', error);
            alert("❌ Une erreur s'est produite lors de la liaison des segments.");
        }
    };


    // Fonction pour vérifier si un segment est déjà lié
    const isLinkedSegment = (block) => {
        return !!(block.video_segment_file || block.video_file) && !block.is_silent;
    };

    const handleDeleteClick = useCallback(async (e) => {
        e.stopPropagation();

        let segmentsToDelete = [];
        let originalTranscript = [...transcript]; // 📁 Sauvegarde pour restauration

        // 🎯 Déterminer quels segments supprimer
        if (selectedBlocks.length > 0) {
            segmentsToDelete = selectedBlocks;
        } else if (selectedBlock) {
            segmentsToDelete = [selectedBlock];
        }

        if (segmentsToDelete.length === 0) {
            return; // Rien à supprimer
        }

        // 🚫 Vérifier si des segments sont en cours de lipsync
        if (hasProcessingSegments(segmentsToDelete)) {
            alert("⏳ Un ou plusieurs segments sont en cours de traitement lipsync. Impossible de les supprimer pour le moment.");
            return;
        }

        // 📊 DEBUG: Logs pour comprendre ce qui se passe
        console.log('Attempting to delete segments:', segmentsToDelete.map(segment => ({
            segment_file: segment.segment_file,
            speaker: segment.speaker,
            start: segment.start,
            end: segment.end,
            is_video: !!(segment.video_segment_file || segment.video_file),
            is_silent: segment.is_silent
        })));

        console.log('Current transcript segments:', transcript.map(t => ({
            segment_file: t.segment_file,
            speaker: t.speaker,
            start: t.start,
            is_video: !!(t.video_segment_file || t.video_file)
        })));

        // 🔄 Créer le transcript mis à jour AVANT l'appel API
        const updatedTranscript = transcript.filter(block =>
            !segmentsToDelete.some(segmentToDelete =>
                // Comparaison robuste pour identifier le segment
                segmentToDelete.segment_file === block.segment_file ||
                (segmentToDelete.start === block.start &&
                    segmentToDelete.end === block.end &&
                    segmentToDelete.speaker === block.speaker)
            )
        );

        // 🎨 Mettre à jour l'interface immédiatement pour une meilleure UX
        onTranscriptUpdate(updatedTranscript);

        // 🧹 Nettoyer la sélection
        if (selectedBlocks.length > 0) {
            setSelectedBlocks([]);
        }
        if (selectedBlock) {
            onBlockSelect(null);
        }

        // 🌐 Appel API pour chaque segment à supprimer
        try {
            for (const segmentToDelete of segmentsToDelete) {
                const requestBody = {
                    transcript: updatedTranscript, // Envoyer le transcript APRÈS suppression
                    segment_file: segmentToDelete.segment_file,
                    speaker: segmentToDelete.speaker
                };

                console.log('Sending delete request with body:', requestBody);

                const response = await fetch(`${API_CONFIG.BASE_URL}/delete_segment/${projectId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(requestBody)
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Delete segment error:', errorData);
                    throw new Error(errorData.error || `HTTP ${response.status}: Failed to delete segment`);
                }

                console.log('Segment deleted successfully:', segmentToDelete.segment_file);
            }

            // ✅ Sauvegarder seulement si toutes les suppressions ont réussi
            await onSave(updatedTranscript);

            console.log(`Successfully deleted ${segmentsToDelete.length} segment(s)`);

        } catch (error) {
            console.error('Error deleting segments:', error);

            // 🔄 En cas d'erreur, restaurer le transcript original
            onTranscriptUpdate(originalTranscript);

            // 🔄 Restaurer la sélection si nécessaire
            if (segmentsToDelete.length === 1) {
                onBlockSelect(segmentsToDelete[0]);
            } else if (segmentsToDelete.length > 1) {
                setSelectedBlocks(segmentsToDelete);
            }

            // 🚨 Afficher l'erreur à l'utilisateur
            alert(`❌ Erreur lors de la suppression: ${error.message}`);
            return;
        }

        // 🔄 Vérifier la disponibilité d'annulation après suppression réussie
        if (typeof checkUndoAvailability === 'function') {
            setTimeout(() => {
                checkUndoAvailability();
            }, 500);
        }

    }, [transcript, selectedBlocks, selectedBlock, onTranscriptUpdate, onBlockSelect, projectId, onSave, hasProcessingSegments, setSelectedBlocks, checkUndoAvailability]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Delete') {
                handleDeleteClick(event);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleDeleteClick]);


    // Auto scroll 
    useEffect(() => {
        if (isPlaying && !isCutMode && !isDragging && !isMouseDown && timelineRef.current) {
            const timelineWidth = timelineRef.current.clientWidth;
            const totalWidth = timelineRef.current.scrollWidth;
            const currentScrollLeft = timelineRef.current.scrollLeft;
            const cursorPositionPixel = (cursorPosition / duration) * totalWidth;
            const visibleLeftEdge = currentScrollLeft;
            const visibleRightEdge = currentScrollLeft + timelineWidth;
            const scrollMargin = timelineWidth * 0.2;

            // Vérifier si le curseur est hors de la zone visible avec une marge
            if (cursorPositionPixel < visibleLeftEdge + scrollMargin ||
                cursorPositionPixel > visibleRightEdge - scrollMargin) {

                // Calculer la nouvelle position de scroll
                const newScrollLeft = Math.max(
                    0,
                    Math.min(
                        totalWidth - timelineWidth,
                        cursorPositionPixel - (timelineWidth / 2) // Centrer le curseur
                    )
                );

                // Utiliser scrollTo avec behavior: 'smooth' pour un défilement fluide
                timelineRef.current.scrollTo({
                    left: newScrollLeft,
                    behavior: 'smooth'
                });
            }
        }
    }, [currentTime, isCutMode, isDragging, isMouseDown, duration, cursorPosition]);

    const handleMergeBlocks = async () => {
        if (selectedBlocks.length !== 2) {
            alert("⚠️ Veuillez sélectionner exactement deux blocs à fusionner.");
            return;
        }

        const [block1, block2] = selectedBlocks;

        // 🚫 NOUVEAU : Vérifier si un des segments est en cours de lipsync
        if (hasProcessingSegments([block1, block2])) {
            alert("⏳ Un ou plusieurs segments sont en cours de traitement lipsync. Impossible de les fusionner pour le moment.");
            return;
        }

        // 🚫 VÉRIFICATION : Empêcher la fusion si les blocs sont sur la piste VIDEO
        if (block1.speaker === 'VIDEO' || block2.speaker === 'VIDEO') {
            alert("❌ Impossible de fusionner des segments de la piste vidéo.");
            return;
        }

        // 🚫 VÉRIFICATION : Empêcher la fusion si les blocs n'ont pas le même speaker
        if (block1.speaker !== block2.speaker) {
            alert("❌ Impossible de fusionner des segments de speakers différents.");
            return;
        }

        // 🎥 NOUVEAU : Vérifier si les deux segments ont des vidéos
        const hasLinkedVideo = isVideoSegment(block1) && isVideoSegment(block2);
        const mixedVideoStatus = isVideoSegment(block1) !== isVideoSegment(block2);

        // 🚫 VÉRIFICATION : Empêcher la fusion si un seul segment a une vidéo
        if (mixedVideoStatus) {
            alert("❌ Impossible de fusionner un segment avec vidéo et un segment sans vidéo.");
            return;
        }

        // 🎥 INFO : Informer l'utilisateur si on va fusionner audio + vidéo
        if (hasLinkedVideo) {
            console.log("🎬 Merging two segments with linked videos");
        }

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/merge_blocks`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    project_id: projectId,
                    transcript: transcript,
                    block1SegmentFile: block1.segment_file,
                    block2SegmentFile: block2.segment_file,
                    prioritizeOriginalSpeaker: true
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();

            // 🎥 NOUVEAU : Vérifier si le backend a fusionné des vidéos
            if (result.hadVideo) {
                console.log("✅ Videos were successfully merged along with audio");
            }

            // Identifier uniquement le nouveau segment fusionné
            const modifiedSegments = findModifiedSegments(transcript, result.updatedTranscript);

            // Mettre à jour le transcript complet
            onTranscriptUpdate(result.updatedTranscript);

            // Recharger uniquement le nouveau segment fusionné
            if (modifiedSegments.length > 0) {
                const newSegment = modifiedSegments[0];
                if (handleAudioReload) {
                    const audioPathSpeaker = newSegment.original_speaker || newSegment.speaker;
                    handleAudioReload(audioPathSpeaker, newSegment);
                }
            }

            // Vérifier la disponibilité d'annulation après l'opération de fusion
            if (typeof checkUndoAvailability === 'function') {
                setTimeout(() => {
                    checkUndoAvailability();
                }, 500);
            }

            // ✅ Réinitialiser la sélection après fusion réussie
            setSelectedBlocks([]);

        } catch (error) {
            console.error('Error:', error);
            alert("❌ Une erreur s'est produite lors de la fusion des blocs.");
        }
    };


    // Ajouter cette fonction utilitaire dans votre composant Timeline
    const isVideoSegment = (block) => {
        if (!block) return false; // 🛡️ PROTECTION AJOUTÉE
        return !!(block.video_segment_file || block.video_file);
    };

    // Fonction pour vérifier si un speaker a des segments vidéo
    const speakerHasVideoSegments = (speaker, transcript) => {
        if (!speaker || !Array.isArray(transcript)) return false; // 🛡️ PROTECTION AJOUTÉE
        return transcript.some(segment =>
            isValidSegment(segment) && // 🛡️ VÉRIFICATION AJOUTÉE
            segment.speaker === speaker &&
            isVideoSegment(segment)
        );
    };

    const renderVideoBlock = (block, index, speaker) => {
        const isInSelection = selectedBlocks.includes(block);
        const isHovered = hoveredBlock === block;
        const left = `${(block.start / duration) * 100}%`;
        const width = `${((block.end - block.start) / duration) * 100}%`;
        const baseColor = getColor(speaker);
        const color = isInSelection ? '#6366f1' : isHovered ? lightenColor(PRIMARY_COLOR, 40) : baseColor;
        const borderColor = lightenColor(color, 40);

        // Style différent pour les segments silencieux
        const opacity = block.is_silent ? 0.6 : 0.9;

        return (
            <Tooltip
                key={index}
                title={
                    <div>
                        <div>{block.is_silent ? 'Segment vidéo silencieux' : 'Segment vidéo'}</div>
                        <div>{formatTime(block.start)} - {formatTime(block.end)}</div>
                    </div>
                }
                placement="top"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: left,
                        width: width,
                        height: '100%',
                        bgcolor: color,
                        opacity: opacity,
                        cursor: isCutMode ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: `1px solid ${borderColor}`,
                        boxSizing: 'border-box',
                        borderRadius: '2px',
                        boxShadow: isInSelection ? '0 0 0 1px rgba(255,255,255,0.5)' : 'none',
                        '&:hover': {
                            boxShadow: '0 0 0 1px rgba(255,255,255,0.7)',
                        },
                    }}
                    onClick={(e) => {
                        // 🎯 IMPORTANT: Ne pas gérer le click en mode cut pour les vidéos
                        if (!isCutMode) {
                            handleBlockSelect(block, e);
                        } else {
                            // En mode cut sur vidéo, empêcher la propagation
                            e.stopPropagation();
                        }
                    }}
                    onMouseEnter={() => setHoveredBlock(block)}
                    onMouseLeave={() => setHoveredBlock(null)}
                >
                    <VideocamIcon
                        sx={{
                            fontSize: '1.2rem',
                            color: 'white',
                            opacity: block.is_silent ? 0.5 : 0.8,
                            filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))',
                        }}
                    />
                    {block.is_silent && (
                        <VolumeOffIcon
                            sx={{
                                fontSize: '0.8rem',
                                color: 'white',
                                opacity: 0.7,
                                position: 'absolute',
                                bottom: 2,
                                right: 2,
                            }}
                        />
                    )}
                </Box>
            </Tooltip>
        );
    };

    const renderBlock = (block, index, speaker) => {
        const isInSelection = selectedBlocks.includes(block);
        const isHovered = hoveredBlock === block;
        const isProcessing = isSegmentProcessingLipsync(block); // 🚫 NOUVEAU
        const left = `${(block.start / duration) * 100}%`;
        const width = `${((block.end - block.start) / duration) * 100}%`;
        const baseColor = getColor(speaker);

        // 🎨 NOUVEAU : Couleur différente pour les segments en cours de traitement
        const color = isProcessing ? '#FFA500' : // Orange pour le traitement
            isInSelection ? '#6366f1' :
                isHovered ? lightenColor(PRIMARY_COLOR, 40) : baseColor;
        const borderColor = lightenColor(color, 40);
        const textToShow = showTranslatedText ? block.translated_text : block.text;

        const gain = block.audio_gain !== undefined ? block.audio_gain : 1.0;
        const opacityBasedOnGain = 0.5 + (gain * 0.5); // Entre 0.5 et 1.0 pour la visibilité

        return (
            <Tooltip
                key={index}
                title={
                    <div>
                        <div>{textToShow}</div>
                    </div>
                }
                placement="top"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: left,
                        width: width,
                        height: '100%',
                        bgcolor: color,
                        opacity: isProcessing ? 0.7 : 0.9, // 🎨 NOUVEAU : Moins opaque si en traitement
                        cursor: isProcessing ? 'not-allowed' : // 🚫 NOUVEAU : Curseur différent
                            isCutMode ? 'crosshair' :
                                ((block.video_segment_file || block.video_file) ? 'pointer' : 'move'),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                        border: `1px solid ${borderColor}`,
                        boxSizing: 'border-box',
                        borderRadius: '2px',
                        boxShadow: isInSelection ? '0 0 0 1px rgba(255,255,255,0.5)' : 'none',
                        // 🎨 NOUVEAU : Animation pour les segments en traitement
                        animation: isProcessing ? 'pulse 1.5s ease-in-out infinite' : 'none',
                        '&:hover': {
                            boxShadow: isProcessing ? 'none' : '0 0 0 1px rgba(255,255,255,0.7)',
                        },
                    }}
                    onMouseDown={(e) => {
                        if (!isCutMode && !isProcessing) { // 🚫 NOUVEAU : Bloquer si en traitement
                            handleMouseDown(e, block);
                        }
                    }}
                    onClick={(e) => {
                        if (!isCutMode && !isProcessing) { // 🚫 NOUVEAU : Bloquer si en traitement
                            handleBlockSelect(block, e);
                        }
                    }}
                    onMouseEnter={() => setHoveredBlock(block)}
                    onMouseLeave={() => setHoveredBlock(null)}
                >
                    <WaveformDisplay
                        ref={(el) => {
                            const key = `${block.speaker}-${block.segment_file}`;
                            if (!waveformRefs.current) waveformRefs.current = {};
                            waveformRefs.current[key] = el;
                        }}
                        waveformFile={block.waveform}
                        color={color}
                        projectId={projectId}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            padding: '2px',
                            fontSize: '10px',
                            color: 'white',
                            textShadow: '0px 0px 2px rgba(0,0,0,0.7)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                        }}
                    >
                        {textToShow}
                    </Box>

                </Box>
            </Tooltip>
        );
    };

    const handleGainChange = (value) => {
        if (!selectedBlock) return;

        const updatedTranscript = transcript.map(block => {
            if (block === selectedBlock) {
                return { ...block, audio_gain: value };
            }
            return block;
        });

        onTranscriptUpdate(updatedTranscript);
        // Pas besoin de sauvegarder immédiatement, on le fera quand l'utilisateur relâche le slider
    };

    const handleGainChangeCommitted = () => {
        if (selectedBlock) {
            onSave();
        }
    };

    const formatSpeakerName = (speaker) => {
        // ➕ AJOUTER CETTE CONDITION :
        if (speaker === 'VIDEO') {
            return 'VIDEO';
        }
        if (speaker.startsWith('SPEAKER')) {
            const number = parseInt(speaker.split('_')[1]) + 1;
            return `SPEAKER ${number}`;
        }
        if (speaker.startsWith('BACKGROUND')) {
            const number = parseInt(speaker.split('_')[1]) + 1;
            return `BACKGROUND ${number}`;
        }
        return speaker;
    };

    return (
        <Box ref={containerRef} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#1E1E1E', userSelect: 'none' }}>
            <style jsx>{`
            @keyframes pulse {
                0%, 100% { opacity: 0.7; }
                50% { opacity: 0.9; }
            }
        `}</style>
            <Box sx={{ display: 'flex', flexGrow: 1, height: '100%' }}>
                {/* Section des speakers */}
                <Box sx={{
                    width: SPEAKER_LABEL_WIDTH,
                    flexShrink: 0,
                    bgcolor: '#2D2D2D',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* En-tête vide */}
                    <Box sx={{
                        height: TIMESCALE_HEIGHT + 10,
                        borderBottom: '1px solid #3A3A3A',
                        borderRight: '1px solid #3A3A3A'
                    }} />

                    {/* Liste des speakers */}
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        {speakerOrder.map((speaker) => (
                            <Box
                                key={speaker}
                                sx={{
                                    height: TRACK_HEIGHT,
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    borderBottom: '1px solid #3A3A3A',
                                    borderRight: '1px solid #3A3A3A',
                                    justifyContent: 'space-between'
                                }}
                            >

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* Icône conditionnelle : Vidéo pour VIDEO, Volume pour les autres */}
                                    {speaker === 'VIDEO' ? (
                                        // 🎬 Icône vidéo pour la piste VIDEO (non-interactive)
                                        <Box
                                            sx={{
                                                padding: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: '32px',
                                                minHeight: '32px',
                                            }}
                                        >
                                            <VideocamIcon
                                                fontSize="small"
                                                sx={{
                                                    color: 'rgba(255,255,255,0.7)',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        // 🔊 Icône de volume pour les autres pistes (interactive)
                                        <IconButton
                                            onClick={() => toggleMute(speaker)}
                                            size="small"
                                            sx={{
                                                color: 'rgba(255,255,255,0.6)',
                                                padding: '4px',
                                                '&:hover': {
                                                    color: PRIMARY_COLOR
                                                }
                                            }}
                                        >
                                            {/* ✅ CORRECTION : Afficher le bon état en tenant compte de isVideoAudioEnabled */}
                                            {(mutedTracks[speaker] || isVideoAudioEnabled) ?
                                                <VolumeOffIcon fontSize="small" /> :
                                                <VolumeUpIcon fontSize="small" />
                                            }
                                        </IconButton>
                                    )}

                                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                                        {formatSpeakerName(speaker)}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={() => removeSpeaker(speaker)}
                                    size="small"
                                    disabled={speakerOrder.length <= 1 || speaker === 'VIDEO'}
                                    sx={{
                                        color: 'rgba(255,255,255,0.6)',
                                        padding: '4px',
                                        opacity: (speakerOrder.length <= 1 || speaker === 'VIDEO') ? 0.3 : 1,
                                        '&:hover': {
                                            color: speaker === 'VIDEO' ? 'rgba(255,255,255,0.3)' : '#FF6B6B'
                                        },
                                        '&.Mui-disabled': {
                                            opacity: 0.3
                                        }
                                    }}
                                >
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>

                    {/* Bouton d'ajout de piste en bas */}
                    <Box
                        sx={{
                            borderTop: '1px solid #3A3A3A',
                            borderRight: '1px solid #3A3A3A',
                            py: 1
                        }}
                    >
                        <Button
                            onClick={handleAddSpeaker}
                            startIcon={<AddIcon />}
                            sx={{
                                width: '90%',
                                mx: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                py: 0.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '& .MuiSvgIcon-root': {
                                        color: PRIMARY_COLOR
                                    }
                                }
                            }}
                        >
                            Add track
                        </Button>
                    </Box>
                </Box>

                {/* Section de la timeline */}
                <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
                    <Box
                        ref={timelineRef}
                        sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: '#2D2D2D',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            '&::-webkit-scrollbar': {
                                height: '10px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#555',
                                borderRadius: '5px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#333',
                                borderRadius: '5px',
                            },
                        }}
                        onScroll={handleScroll}
                        onClick={handleTimelineClick}
                    >
                        <Box sx={{ width: `${100 * zoom}%`, height: '100%', position: 'relative' }}>
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: TIMESCALE_HEIGHT + 10,
                                borderBottom: '1px solid #3A3A3A',
                                zIndex: 1
                            }}>
                                {renderTimeScale()}
                            </Box>
                            {speakerOrder.map((speaker, speakerIndex) => (
                                <Box
                                    key={speaker}
                                    sx={{
                                        position: 'absolute',
                                        top: speakerIndex * TRACK_HEIGHT + TIMELINE_PADDING + TIMESCALE_HEIGHT,
                                        left: 0,
                                        width: '100%',
                                        height: TRACK_HEIGHT,
                                        bgcolor: '#252525',
                                        borderBottom: '1px solid #3A3A3A',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {speaker === 'VIDEO' ? (
                                        transcript
                                            .filter(t =>
                                                isValidSegment(t) && // 🛡️ SÉCURITÉ CRITIQUE
                                                (t.video_segment_file || t.video_file)
                                            ).map((t, tIndex) => renderVideoBlock(t, tIndex, speaker))
                                    ) : (
                                        transcript
                                            .filter(t =>
                                                isValidSegment(t) && // 🛡️ SÉCURITÉ CRITIQUE
                                                t.speaker === speaker &&
                                                (showSilentSegments || t.is_silent !== true)
                                            ).map((t, tIndex) => renderBlock(t, tIndex, speaker))
                                    )}
                                </Box>
                            ))}

                            {speakerOrder.map((speaker, speakerIndex) => (
                                <Box
                                    key={speaker}
                                    sx={{
                                        position: 'absolute',
                                        top: speakerIndex * TRACK_HEIGHT + TIMELINE_PADDING + TIMESCALE_HEIGHT,
                                        left: 0,
                                        width: '100%',
                                        height: TRACK_HEIGHT,
                                        bgcolor: '#252525',
                                        borderBottom: '1px solid #3A3A3A',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {speaker === 'VIDEO' ? (
                                        // ✅ Piste VIDEO : afficher uniquement les segments vidéo
                                        transcript
                                            .filter(t =>
                                                (t.video_segment_file || t.video_file)
                                                // Toujours afficher TOUS les segments vidéo (silencieux ou non)
                                            )
                                            .map((t, tIndex) => renderVideoBlock(t, tIndex, speaker))
                                    ) : (
                                        // ✅ Pistes audio (SPEAKER/BACKGROUND) : afficher TOUS les segments de ce speaker
                                        transcript
                                            .filter(t =>
                                                t.speaker === speaker &&
                                                // ✅ CORRECTION : Ne plus exclure les segments qui ont une vidéo liée
                                                // Maintenant on affiche TOUS les segments du speaker (avec ou sans vidéo)
                                                (showSilentSegments || t.is_silent !== true) // Filtrer seulement les segments silencieux si nécessaire
                                            )
                                            .map((t, tIndex) => renderBlock(t, tIndex, speaker))
                                    )}
                                </Box>
                            ))}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${(cursorPosition / duration) * 100}%`,
                                    top: 0,
                                    bottom: 0,
                                    width: CURSOR_WIDTH,
                                    bgcolor: isCutMode
                                        ? (isCutProcessing ? '#999999' : '#FF6B6B') // Gris si en cours de traitement, rouge sinon
                                        : PRIMARY_COLOR,
                                    opacity: isCutProcessing && isCutMode ? '0.5' : '0.8', // Moins opaque si en traitement
                                    pointerEvents: 'none',
                                    zIndex: 2,
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Barre d'outils en bas */}
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: '#2D2D2D' }}>
                <IconButton size="small" onClick={() => handleZoomChange(null, Math.max(1, zoom - 1))} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Slider
                    value={zoom}
                    onChange={handleZoomChange}
                    min={1}
                    max={8}
                    step={0.1}
                    sx={{
                        mx: 1,
                        width: 100,
                        color: PRIMARY_COLOR,
                        '& .MuiSlider-thumb': {
                            width: 12,
                            height: 12,
                        },
                        '& .MuiSlider-rail': {
                            opacity: 0.3,
                        },
                    }}
                />
                <IconButton size="small" onClick={() => handleZoomChange(null, Math.min(20, zoom + 1))} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    <ZoomInIcon fontSize="small" />
                </IconButton>
                <Tooltip
                    title={isCutMode ? "Mode coupe activé - Les segments vidéo ne peuvent pas être coupés" : "Activer le mode coupe"}
                    placement="top"
                >
                    <IconButton
                        size="small"
                        onClick={handleCutModeToggle}
                        disabled={isCutProcessing}
                        sx={{
                            ml: 2,
                            color: isCutMode ? '#FF6B6B' : 'rgba(255,255,255,0.7)',
                            opacity: isCutProcessing ? 0.5 : 1,
                        }}
                    >
                        <ContentCutIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <IconButton
                    size="small"
                    onClick={handleDeleteClick}
                    disabled={!selectedBlock && selectedBlocks.length === 0}
                    sx={{
                        ml: 2,
                        color: selectedBlock || selectedBlocks.length > 0 ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)',
                        '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.3)',
                        }
                    }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={toggleTranslatedText} sx={{ ml: 2, color: showTranslatedText ? PRIMARY_COLOR : 'rgba(255,255,255,0.7)' }}>
                    <TranslateIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={handleMergeBlocks}
                    disabled={
                        selectedBlocks.length !== 2 ||
                        selectedBlocks.some(block => block.speaker === 'VIDEO') ||
                        selectedBlocks[0].speaker !== selectedBlocks[1].speaker ||
                        // 🎥 NOUVEAU : Empêcher la fusion si un seul bloc a une vidéo
                        (isVideoSegment(selectedBlocks[0]) !== isVideoSegment(selectedBlocks[1]))
                    }
                    sx={{
                        ml: 2,
                        color: selectedBlocks.length === 2 &&
                            !selectedBlocks.some(block => block.speaker === 'VIDEO') &&
                            selectedBlocks[0].speaker === selectedBlocks[1].speaker &&
                            // 🎥 NOUVEAU : Les deux doivent avoir le même statut vidéo
                            (isVideoSegment(selectedBlocks[0]) === isVideoSegment(selectedBlocks[1]))
                            ? PRIMARY_COLOR
                            : 'rgba(255,255,255,0.3)',
                        '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.3)',
                        }
                    }}
                >
                    <MergeTypeIcon fontSize="small" />
                </IconButton>
                <Tooltip
                    title={
                        selectedBlocks.length === 2 && canLinkSegments(selectedBlocks)
                            ? "Lier l'audio et la vidéo"
                            : selectedBlock && isVideoSegment(selectedBlock) && !selectedBlock.is_silent
                                ? "Séparer l'audio et la vidéo"
                                : "Sélectionnez un segment vidéo+audio ou deux segments à lier"
                    }
                    placement="top"
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => {
                                // Si on a deux blocs sélectionnés qui peuvent être liés
                                if (selectedBlocks.length === 2 && canLinkSegments(selectedBlocks)) {
                                    handleLinkSegments();
                                }
                                // Si on a un seul bloc sélectionné qui est déjà lié
                                else if (selectedBlock && isVideoSegment(selectedBlock) && !selectedBlock.is_silent) {
                                    handleUnlinkSegment();
                                }
                            }}
                            disabled={
                                !(
                                    // Cas 1 : Deux blocs sélectionnés qui peuvent être liés
                                    (selectedBlocks.length === 2 && canLinkSegments(selectedBlocks) && !hasProcessingSegments(selectedBlocks)) ||
                                    // Cas 2 : Un bloc sélectionné qui peut être délié ET n'est pas en cours de lipsync
                                    (selectedBlock && isVideoSegment(selectedBlock) && !selectedBlock.is_silent && !isSegmentProcessingLipsync(selectedBlock))
                                )
                            }
                            sx={{
                                ml: 2,
                                color: (
                                    (selectedBlocks.length === 2 && canLinkSegments(selectedBlocks) && !hasProcessingSegments(selectedBlocks)) ||
                                    (selectedBlock && isVideoSegment(selectedBlock) && !selectedBlock.is_silent && !isSegmentProcessingLipsync(selectedBlock))
                                ) ? PRIMARY_COLOR : 'rgba(255,255,255,0.3)',
                                '&.Mui-disabled': {
                                    color: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <LinkOffIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton
                    size="small"
                    onClick={toggleSubtitles}
                    sx={{
                        ml: 2,
                        color: areSubtitlesEnabled ? PRIMARY_COLOR : 'rgba(255,255,255,0.7)'
                    }}
                >
                    {areSubtitlesEnabled ? <SubtitlesIcon fontSize="small" /> : <SubtitlesOffIcon fontSize="small" />}
                </IconButton>
                {hasVideo && (
                    <Box
                        onClick={toggleVideoAudio}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ml: 2,
                            cursor: 'pointer',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255,255,255,0.7)',
                            borderRadius: '20px',
                            padding: '2px 10px',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {isVideoAudioEnabled ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                        <Typography
                            variant="caption"
                            sx={{
                                ml: 0.8,
                                mt: 0.30,
                                fontSize: '0.70rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {isVideoAudioEnabled ? 'Original voice' : 'IA voice'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box >
    );
}

export default Timeline;